import { Link } from "react-router-dom";
import ContentPage, { Eyebrow, PageTitle, Lead, H2, P, UL } from "@/components/ContentPage";
import { useSeo } from "@/lib/seo";

export default function HowScoreWorks() {
  useSeo({
    title: "How the Financial Future Readiness Score Works",
    description: "Learn how the 0–100 Financial Future Readiness Score evaluates tax planning, future income, asset management, protection, and estate planning.",
    canonicalPath: "/how-the-score-works",
  });
  return (
    <ContentPage
      breadcrumbs={[
        { to: "/", label: "Home" },
        { to: "/how-the-score-works", label: "How Your Score Is Calculated" },
      ]}
      readMinutes={4}
    >
      <Eyebrow>How It Works</Eyebrow>
      <PageTitle>How Your Financial Future Readiness Score Works</PageTitle>
      <Lead>
        The Financial Future Readiness Score is an educational planning framework, not an investment forecast or a financial recommendation.
        It uses a deterministic set of rules to translate your answers into a 0–100 score across five planning areas.
      </Lead>

      <H2>The five categories</H2>
      <P><strong>Tax Planning (max 20 points).</strong> Whether taxes are being considered as part of overall financial decisions — account tax treatment, long-term tax diversification, and coordination with retirement planning where relevant.</P>
      <P><strong>Future Income &amp; Retirement Planning (max 25 points).</strong> For younger users this evaluates savings, goals, cash-flow awareness, inflation, and long-term preparedness. For people approaching retirement it evaluates expected spending, income sources, withdrawal strategy, longevity, and stress planning.</P>
      <P><strong>Asset Management (max 25 points).</strong> Liquidity, diversification, debt and cash-flow health where applicable, investment risk alignment, and how recently the strategy has been reviewed.</P>
      <P><strong>Insurance &amp; Protection (max 15 points).</strong> Whether the household is considering risks such as income interruption, health events, long-term care, and survivor issues appropriate to the person's life stage.</P>
      <P><strong>Legacy &amp; Estate Planning (max 15 points).</strong> Beneficiaries, will, powers of attorney, healthcare directives, and clarity around legacy intentions.</P>
      <P>Each category has its own maximum. Total possible score: 100.</P>

      <H2>What the score does not mean</H2>
      <UL>
        <li>The score does not predict investment performance.</li>
        <li>The score is not a probability of financial success.</li>
        <li>The score is not investment advice.</li>
        <li>The score is not tax, legal, or insurance advice.</li>
        <li>The score is not an industry ranking or percentile against other users.</li>
      </UL>
      <P>
        Think of the score as a way to organize planning questions worth exploring — not as a grade of your finances.
        A stronger score suggests planning appears more coordinated in the areas we evaluate; a lower score simply highlights topics that may be worth a closer look.
      </P>

      <H2>Life-stage adaptation</H2>
      <P>
        The assessment adapts its questions and result copy based on where you are in life. Someone more than ten years from retirement
        receives wealth-building oriented questions; someone within a few years of retirement — or already retired — receives
        retirement-focused questions such as withdrawal strategy, longevity, and inflation.
      </P>

      <P className="mt-8">
        Curious how the categories apply at different life stages? See our{" "}
        <Link to="/financial-planning-20s-30s" className="underline" style={{ color: "#258FC4" }}>20s &amp; 30s guide</Link>,{" "}
        <Link to="/financial-planning-40s-50s" className="underline" style={{ color: "#258FC4" }}>40s &amp; 50s guide</Link>, or the{" "}
        <Link to="/retirement-readiness-guide" className="underline" style={{ color: "#258FC4" }}>retirement readiness guide</Link>.
      </P>
    </ContentPage>
  );
}
